import React, { CSSProperties, ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import RequestHelper from "../../helpers/RequestHelper";
import Columns from "../../library/components/layout/Columns";
import Rows from "../../library/components/layout/Rows";
import { getGenelDurumString } from "../../features/loadingMessageSlice";
import { getCurrentUser } from "../../features/currentUserSlice";
import GenelDurumHelper, { GenelDurumLoginStateHelper } from "../../helpers/GenelDurumHelper";
import DateTimeHelper from "../../library/helpers/DateTimeHelper";
import { BusinessHelper } from "../../helpers/BusinessHelper";
import { CsmText } from "../../library/components/mui/CsmText";
import { IMainScreenProps, MainLayout } from "../../components/layout/MainLayout";
import { mainScreenLocations } from "../../app/mainScreens";
import { Badge, Box, Button, alpha } from "@mui/material";
import AlertHelper from "../../library/helpers/AlertHelper";
import { CalismaModel } from "../../models/businessModels/CalismaModel";
import { appIcons } from "../../app/appIcons";
import { VatanCalendarSizes } from "../../components/vatan/VatanCalendar";
import appColors from "../../app/appColors";
import { HesapCalismaModel } from "../../models/businessModels/HesapCalismaModel";
import { EnmKatilimBildirmeDurum } from "../../models/enums/EnmKatilimBildirmeDurum";
import YoklamaBildirmePopupEditor from "./takvim/YoklamaBildirmePopupEditor";
import { EnmKatilimDurum } from "../../models/enums/EnmKatilimDurum";
import { HesapBilgiModel } from "../../models/businessModels/HesapBilgiModel";
import { SesGrupModel } from "../../models/businessModels/SesGrupModel";
import { HesapBilgiVeKullaniciModel } from "../../models/businessModels/HesapBilgiVeKullaniciModel";
import VatanCalendarWithNavigation from "../../components/vatan/VatanCalendarWithNavigation";
import { EnmYetki } from "../../models/enums/EnmYetki";
import { useTranslation } from "react-i18next";
import TranslationHelper from '../../helpers/TranslationHelper';
import StringHelper from "../../library/helpers/StringHelper";
import DateTimeToStringHelper from "../../library/helpers/DateTimeToStringHelper";

function TakvimScreen() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const translate = TranslationHelper.TransFunc(t);
    const requestHelper = RequestHelper(navigate, dispatch, translate);

    const genelDurum = GenelDurumHelper.StringToModel(useAppSelector(getGenelDurumString)!)!;
    const user = useAppSelector(getCurrentUser);

    useEffect(() => {
        if (!user.token || genelDurum == null) {
            GenelDurumLoginStateHelper.GenelDurumYukleVeKoroAyarla(requestHelper, navigate, dispatch, user, genelDurum);
        }
    });

    if (!user.token || genelDurum == null) {
        return <></>;
    }

    const genelDurumHelper = new GenelDurumHelper(genelDurum, user, translate, navigate, dispatch, i18n, requestHelper);

    return <TakvimScreenInner genelDurumHelper={genelDurumHelper} />
}

function TakvimScreenInner(props: IMainScreenProps) {
    const { genelDurumHelper } = props;

    const [seciliGun, setSeciliGun] = useState<Date | undefined>(undefined);

    const etkinlikTakivimiKullanilsin = genelDurumHelper.SeciliKoroModel.koroAyarlari.etkinlikTakivimiKullanilsin;
    const tumCalismalariGorebilir = genelDurumHelper.HasYetkiAny([EnmYetki["Calışma Yönetim"], EnmYetki["Calışma İzleme"]]) || !genelDurumHelper.SeciliKoroModel.koroAyarlari.koristlerSadeceSorumluCalismalariGorebilir;

    const tumCalismalar = !etkinlikTakivimiKullanilsin ? genelDurumHelper.SeciliKoroModel.tumCalismalar : genelDurumHelper.SeciliKoroModel.tumCalismalar.where(e => e.sezonModelId === genelDurumHelper.AktifSezon.id);

    const zorunluCalismalar = BusinessHelper.Calisma.KisiyeUygunCalismalariGetir(tumCalismalar, genelDurumHelper.SeciliHesapBilgiModel, genelDurumHelper.SeciliKoroModel.hesapCalismaModelList, false, genelDurumHelper.SeciliKoroModel.tumSesGruplari);
    const gorebilecegiCalismalar = BusinessHelper.Calisma.KisiyeUygunCalismalariGetir(tumCalismalar, genelDurumHelper.SeciliHesapBilgiModel, genelDurumHelper.SeciliKoroModel.hesapCalismaModelList, tumCalismalariGorebilir, genelDurumHelper.SeciliKoroModel.tumSesGruplari);

    const yoklamaArabirimiKullanilsin = genelDurumHelper.SeciliKoroModel.koroAyarlari.yoklamaArabirimiKullanilsin;

    const sonKayitTarihliSarkilar = genelDurumHelper.SeciliKoroModel.tumSarkilar.selectMany(e => e.sezonSarkiModelList.select(t => ({ sarki: e, sezonId: t.sezonModelId, tarih: t.kayitTarihi }))).where(e => e.tarih !== null);

    // etkinlik takvimi hesapları
    const etkinlikTakvimCalismalar = tumCalismalar;

    const calismalarinMinSaati = !etkinlikTakivimiKullanilsin ? 0 : etkinlikTakvimCalismalar.select(e => e.tarihBaslangic.getHours()).min(e => e) ?? 10;
    const calismalarinMaxSaati = !etkinlikTakivimiKullanilsin ? 0 : etkinlikTakvimCalismalar.select(e => e.tarihBitis.getHours()).max(e => e) ?? 16;
    const gunDakikaTepe = !etkinlikTakivimiKullanilsin ? 0 : (calismalarinMinSaati - 1) * 60;
    const gunDakikaAlt = !etkinlikTakivimiKullanilsin ? 0 : (calismalarinMaxSaati + 1) * 60;
    const gunDakikaYukseklik = gunDakikaAlt - gunDakikaTepe;

    const etkinlik_siraliCalismalar = !etkinlikTakivimiKullanilsin ? [] : etkinlikTakvimCalismalar.orderBy(e => e.tarihBaslangic);
    const etkinlik_ilkCalismaTarih = etkinlik_siraliCalismalar.firstOrDefault()?.tarihBaslangic ?? DateTimeHelper.today();
    const etkinlik_sonCalismaTarih = etkinlik_siraliCalismalar.lastOrDefault()?.tarihBaslangic ?? DateTimeHelper.addDays(DateTimeHelper.today(), 1);
    const etkinlik_gunCount = DateTimeHelper.substractForDays(etkinlik_ilkCalismaTarih, etkinlik_sonCalismaTarih) + 1
    const etkinlik_mondays = etkinlik_siraliCalismalar.select(e => DateTimeHelper.getDateWithoutTime(DateTimeHelper.getMondayOfWeek(e.tarihBaslangic)).valueOf()).distinct().select(e => new Date(e));
    const etkinlik_uzun = etkinlik_gunCount < 7 || etkinlik_mondays.length <= 2;


    function isZorunluCalisma(calismaId: string) {
        return zorunluCalismalar.any(e => e.id === calismaId);
    }

    async function katilimDurumBildir(calisma: CalismaModel, hesapCalisma: HesapCalismaModel | null) {
        const response = await AlertHelper.ShowClosableContent<HesapCalismaModel>(`Yoklama Durum Bildir`, (closer) =>
            <YoklamaBildirmePopupEditor
                closer={closer}
                caslima={calisma}
                genelDurumHelper={genelDurumHelper}
                orijinalHesapCalisma={hesapCalisma}
            />
        );

        if (response == null)
            return;

        GenelDurumLoginStateHelper.GenelDurumStateGuncelle(genelDurumHelper.dispatch, genelDurumHelper.genelDurum,
            (newGenelDurum) => {
                const seciliKoro = BusinessHelper.Genel.SeciliKoroGetir(newGenelDurum, genelDurumHelper.user)!;
                const seciliHesap = seciliKoro.hesapBilgiModel;
                const hesapCalismaList = seciliKoro.hesapCalismaModelList;
                const itemToRemove = hesapCalismaList.singleOrDefault(e => e.calismaModelId === calisma.id && e.hesapModelId === seciliHesap.id);

                if (itemToRemove)
                    hesapCalismaList.remove(itemToRemove);
                hesapCalismaList.push(response!);
            });
    }

    function anaSesGrupGetir(hesap: HesapBilgiModel) {
        return BusinessHelper.SesGrubu.HesabinAnaSesGrubunuGetir(genelDurumHelper.SesGruplariniGetir(hesap.sesGrupIdList))!;
    }

    // react renders

    function generateYonetimYoklamaContent(seciliCalismaId: string) {
        const seciliKoro = genelDurumHelper.SeciliKoroModel;
        if (seciliKoro.hesapCalismaModelList.all(e => e.hesapModelId === genelDurumHelper.SeciliHesapBilgiModel.id))
            return null;

        const calisma = seciliKoro.tumCalismalar.single(e => e.id === seciliCalismaId);
        const eskiCalisma = calisma.tarihBaslangic < DateTimeHelper.now();

        const hesaplar = seciliKoro.hesapBilgiList;
        const calismaHesaplar = seciliKoro.hesapCalismaModelList.where(e => e.calismaModelId === seciliCalismaId);

        const sesGrupKisilerRowGetir = (sesGrup: SesGrupModel, alakaliHesaplar: HesapBilgiVeKullaniciModel[]) => <CsmText key={sesGrup.id} smallNote>
            <span style={{ textDecoration: "underline" }}>{sesGrup.isim}</span>: {alakaliHesaplar.where(e => anaSesGrupGetir(e)?.id === sesGrup.id).select(e => `${e.kullaniciModel.ad} ${e.kullaniciModel.soyad}`).orderByTurkish(e => e).join(", ")}
        </CsmText>

        if (eskiCalisma) {
            const gelenler = calismaHesaplar.where(e => e.derseGercekKatilimDurum === EnmKatilimDurum.Katıldı || e.derseGercekKatilimDurum === EnmKatilimDurum["Geç Katıldı"]).select(e => e.hesapModelId);
            const gelmeyenler = calismaHesaplar.where(e => e.derseGercekKatilimDurum === EnmKatilimDurum.Katılmadı).select(e => e.hesapModelId);
            const oran = (gelenler.length + gelmeyenler.length) === 0 ? 0 : (100 * gelenler.length / (gelenler.length + gelmeyenler.length)).toFixed(0);

            const gelenHesaplar = hesaplar.where(e => gelenler.contains(e.id));
            const gelmeyenHesaplar = hesaplar.where(e => gelmeyenler.contains(e.id));


            return <>
                <CsmText bold>Yoklama Bilgileri</CsmText>
                <Columns>
                    <CsmText>Koro Katılımı: </CsmText>
                    <CsmText>%{oran} ({gelenler.length} / {(gelenler.length + gelmeyenler.length)})</CsmText>
                </Columns>
                <Rows>
                    <CsmText>Gelenler: </CsmText>
                    {gelenHesaplar.select(e => anaSesGrupGetir(e)).distinct().orderBy(e => e?.sira).map(e => sesGrupKisilerRowGetir(e, gelenHesaplar))}
                </Rows>
                <Rows>
                    <CsmText>Gelmeyenler: </CsmText>
                    {gelmeyenHesaplar.select(e => anaSesGrupGetir(e)).distinct().orderBy(e => e?.sira).map(e => sesGrupKisilerRowGetir(e, gelmeyenHesaplar))}
                </Rows>
            </>
        }
        else {
            const calismayaUygunHesaplarId = BusinessHelper.Calisma.CalismayaUygunKisileriGetir(calisma, hesaplar, false, genelDurumHelper.SeciliKoroModel.tumSesGruplari).select(e => e.id);

            const gelecegimDiyenler = calismaHesaplar.where(e => e.katilimDurum === EnmKatilimBildirmeDurum.Katılacağım).select(e => e.hesapModelId);
            const gelmeyecegimDiyenler = calismaHesaplar.where(e => e.katilimDurum !== EnmKatilimBildirmeDurum.Katılacağım).select(e => e.hesapModelId);
            const bildirmeyenler = calismayaUygunHesaplarId.except(gelecegimDiyenler).except(gelmeyecegimDiyenler);

            const gelecekHesaplar = hesaplar.where(e => gelecegimDiyenler.contains(e.id));
            const gelmeyecekHesaplar = hesaplar.where(e => gelmeyecegimDiyenler.contains(e.id));
            const bildirmeyenHesaplar = hesaplar.where(e => bildirmeyenler.contains(e.id));

            const geleceklerOranMin = (100 * (gelecekHesaplar.length) / calismayaUygunHesaplarId.length).toFixed();
            const geleceklerOranMax = (100 * (gelecekHesaplar.length + bildirmeyenHesaplar.length) / calismayaUygunHesaplarId.length).toFixed();

            return <>
                <CsmText bold>Yoklama Bilgileri</CsmText>
                <Columns>
                    <CsmText>Tahmini Koro Katılımı: </CsmText>
                    <CsmText>%{geleceklerOranMin}-%{geleceklerOranMax}</CsmText>
                </Columns>
                <Rows>
                    <CsmText>Geleceğim Diyenler ({gelecegimDiyenler.length}):</CsmText>
                    {gelecekHesaplar.select(e => anaSesGrupGetir(e)).distinct().orderBy(e => e?.sira).map(e => sesGrupKisilerRowGetir(e, gelecekHesaplar))}
                </Rows>
                <Rows>
                    <CsmText>Gelmeyeceğim/Belirsiz Diyenler ({gelmeyecekHesaplar.length}):</CsmText>
                    {gelmeyecekHesaplar.select(e => anaSesGrupGetir(e)).distinct().orderBy(e => e?.sira).map(e => sesGrupKisilerRowGetir(e, gelmeyecekHesaplar))}
                </Rows>
                <Rows>
                    <CsmText>Durumunu Bildirmeyenler ({bildirmeyenHesaplar.length}):</CsmText>
                    {bildirmeyenHesaplar.select(e => anaSesGrupGetir(e)).distinct().orderBy(e => e?.sira).map(e => sesGrupKisilerRowGetir(e, bildirmeyenHesaplar))}
                </Rows>
            </>
        }
    }

    function isEtkinlikDay(day: Date): boolean {
        const calismalar = gorebilecegiCalismalar.where(e => DateTimeHelper.isSameDate(e.tarihBaslangic, day));

        if (calismalar.length === 0)
            return false;

        return calismalar.any(e => e.etkinlikKonserMi);
    }

    function renderDate(day: Date, size: VatanCalendarSizes): ReactNode | undefined {
        if (!etkinlikTakivimiKullanilsin)
            return renderDateRegular(day, size);
        else
            return renderDateEtkinlik(day, size);
    }

    function renderDateRegular(day: Date, size: VatanCalendarSizes): ReactNode | undefined {
        const calismalar = gorebilecegiCalismalar.where(e => DateTimeHelper.isSameDate(e.tarihBaslangic, day)).orderBy(e => e.tarihBaslangic);
        const gunSarkilari = sonKayitTarihliSarkilar.where(e => DateTimeHelper.isSameDate(e.tarih, day));

        if (calismalar.length === 0 && gunSarkilari.length === 0)
            return undefined;

        const bildirimsizYeniCalismaVar = calismalar.select(item => BusinessHelper.Calisma.KatilimDurumRenkYaziGetir(item, genelDurumHelper.SeciliKoroModel.hesapCalismaModelList, genelDurumHelper.SeciliHesapBilgiModel, yoklamaArabirimiKullanilsin, genelDurumHelper.SeciliKoroModel.tumSesGruplari)).any(e => e.aksiyonLazim === true);
        const bildirimSuresi = genelDurumHelper.SeciliKoroModel.koroAyarlari.calismaYoklamaBildirimOnSuresi;

        const bildirimSiniriIcinde = bildirimSuresi === 0 || DateTimeHelper.isDateInBetween(day, DateTimeHelper.today(), DateTimeHelper.addDays(DateTimeHelper.today(), bildirimSuresi));

        const tekCalismaVar = calismalar.length === 1;

        return <Badge color={bildirimSiniriIcinde ? "error" : "warning"} badgeContent={bildirimsizYeniCalismaVar ? "!" : null}>
            <Box id="osman" sx={{ fontSize: determineSize(size, "7px", "8px", "10px") }}>
                <Rows ml={1} style={{ cursor: "pointer" }}>
                    {calismalar.map((item, i) => {
                        const katilimStil = BusinessHelper.Calisma.KatilimDurumRenkYaziGetir(item, genelDurumHelper.SeciliKoroModel.hesapCalismaModelList, genelDurumHelper.SeciliHesapBilgiModel, yoklamaArabirimiKullanilsin, genelDurumHelper.SeciliKoroModel.tumSesGruplari);

                        return <CsmText hasLightBottomBorder={(gunSarkilari.length > 0) || (i < calismalar.length - 1)} key={item.id} style={{
                            //flex: 1,
                            background: (bildirimSiniriIcinde || !katilimStil.renk) ? katilimStil.renk : alpha(katilimStil.renk!, 0.5),
                            // tek çalışma varsa yükseklik sabitlenmez: uzun açıklama gizlenmek yerine alt satıra taşar
                            height: tekCalismaVar ? undefined : "12px",
                            whiteSpace: tekCalismaVar ? "normal" : undefined,
                            wordBreak: tekCalismaVar ? "break-word" : undefined,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}>
                            {item.icerik}
                        </CsmText>
                    })}
                    {gunSarkilari.map((sarkiWithTarih, i) =>
                        <CsmText key={sarkiWithTarih.sarki.id}
                            hasLightBottomBorder={(i < gunSarkilari.length - 1)}
                            style={{ lineHeight: "normal", color: appColors.LIGHTRED }}
                            italic>
                            {sarkiWithTarih.sarki.isim} kaydı son günü
                        </CsmText>)}
                </Rows>
            </Box>
        </Badge>
    }

    function renderDateEtkinlik(day: Date, size: VatanCalendarSizes): ReactNode | undefined {
        const calismalar = gorebilecegiCalismalar.where(e => DateTimeHelper.isSameDate(e.tarihBaslangic, day)).orderBy(e => e.tarihBaslangic);

        // const siraliCalismalar = calismalar.orderBy(e => e.tarihBaslangic);
        // const ilkCalismaTarih = siraliCalismalar.firstOrDefault()?.tarihBaslangic ?? DateTimeHelper.today();
        // const sonCalismaTarih = siraliCalismalar.lastOrDefault()?.tarihBaslangic ?? DateTimeHelper.addDays(DateTimeHelper.today(), 1);
        // const gunCount= DateTimeHelper.substractForDays(ilkCalismaTarih, sonCalismaTarih) + 1;


        if (calismalar.length === 0)
            return undefined;

        const bildirimsizYeniCalismaVar = calismalar.select(item => BusinessHelper.Calisma.KatilimDurumRenkYaziGetir(item, genelDurumHelper.SeciliKoroModel.hesapCalismaModelList, genelDurumHelper.SeciliHesapBilgiModel, yoklamaArabirimiKullanilsin, genelDurumHelper.SeciliKoroModel.tumSesGruplari)).any(e => e.aksiyonLazim === true);
        const bildirimSuresi = genelDurumHelper.SeciliKoroModel.koroAyarlari.calismaYoklamaBildirimOnSuresi;

        const bildirimSiniriIcinde = bildirimSuresi === 0 || DateTimeHelper.isDateInBetween(day, DateTimeHelper.today(), DateTimeHelper.addDays(DateTimeHelper.today(), bildirimSuresi));

        return <Badge color={bildirimSiniriIcinde ? "error" : "warning"} badgeContent={bildirimsizYeniCalismaVar ? "!" : null}>
            <Box>
                <div style={{ height: etkinlik_uzun ? "555px" : "200px" }}>
                    {calismalar.map((calisma, i) => {
                        const katilimStil = BusinessHelper.Calisma.KatilimDurumRenkYaziGetir(calisma, genelDurumHelper.SeciliKoroModel.hesapCalismaModelList, genelDurumHelper.SeciliHesapBilgiModel, yoklamaArabirimiKullanilsin, genelDurumHelper.SeciliKoroModel.tumSesGruplari);
                        const cakisanCalismalar = calismalar.where(e => calisma.tarihBaslangic >= e.tarihBitis == false && calisma.tarihBitis <= e.tarihBaslangic == false).orderBy(e => e.icerik);
                        const sira = cakisanCalismalar.indexOf(calisma);

                        const tepeDakika = calisma.tarihBaslangic.getHours() * 60 + calisma.tarihBaslangic.getMinutes();
                        const altDakika = calisma.tarihBitis.getHours() * 60 + calisma.tarihBitis.getMinutes();

                        const tepeYuzde = 100 * (tepeDakika - gunDakikaTepe) / gunDakikaYukseklik;
                        const altYuzde = 100 * (altDakika - gunDakikaTepe) / gunDakikaYukseklik;
                        const solYuzde = 100 * sira / cakisanCalismalar.length;
                        const genislik = 100 / cakisanCalismalar.length;

                        return <div key={calisma.id} style={{
                            position: "absolute",
                            top: `${tepeYuzde}%`,
                            height: `${altYuzde - tepeYuzde}%`,
                            left: `${solYuzde}%`,
                            width: `${genislik}%`,
                        }} >
                            <Rows style={{
                                margin: "5px",
                                height: "100%",
                                borderRadius: "5px",
                                borderColor: appColors.GRAY_LIGHT3,
                                borderWidth: "1px",
                                borderStyle: "solid",
                                background: (bildirimSiniriIcinde || !katilimStil.renk) ? katilimStil.renk : alpha(katilimStil.renk!, 0.5),
                            }}>
                                <span style={{ fontSize: determineSize(size, "6px", "8px", "12px"), fontStyle: "italic", background: appColors.GRAY_LIGHT3 }}>
                                    {DateTimeToStringHelper.toTimeString(calisma.tarihBaslangic)}-{DateTimeToStringHelper.toTimeString(calisma.tarihBitis)}
                                </span>
                                <span style={{ fontSize: determineSize(size, "6px", "10px", "14px"), padding: "5px" }}>
                                    {calisma.icerik}
                                </span>
                            </Rows>
                        </div>
                    })}
                </div>
            </Box>
        </Badge>
    }

    let seciliGunIcerik: React.ReactNode = undefined;

    if (seciliGun) {
        const seciliGunCalismalari = gorebilecegiCalismalar.where(e => DateTimeHelper.isSameDate(e.tarihBaslangic, seciliGun));
        const gunSarkilari = sonKayitTarihliSarkilar.where(e => DateTimeHelper.isSameDate(e.tarih, seciliGun));

        seciliGunIcerik = <Rows>
            <CsmText header>{DateTimeToStringHelper.toMonthDayAndYearAndDayOfWeekString(seciliGun, genelDurumHelper.translate)}</CsmText>
            {gunSarkilari.length > 0 && (
                <>
                    <CsmText bold style={{ marginTop: "10px" }} >Kayıt son Günü olan eserler:</CsmText>
                    <ul style={{ marginTop: 0 }}>
                        {gunSarkilari.map(sarki => (<li key={sarki.sarki.id}><CsmText key={sarki.sarki.id}>{sarki.sarki.isim}</CsmText></li>))}
                    </ul>
                </>
            )}
            {seciliGunCalismalari.map(calisma => {
                const hesapCalisma = genelDurumHelper.SeciliKoroModel.hesapCalismaModelList.singleOrDefault(e => e.hesapModelId === genelDurumHelper.SeciliHesapBilgiModel.id && e.calismaModelId === calisma.id);

                const eskiCalisma = calisma.tarihBaslangic < DateTimeHelper.today();
                const calismaZorunluMu = isZorunluCalisma(calisma.id);

                const tarihBaslangic = DateTimeToStringHelper.toTimeString(calisma.tarihBaslangic);
                const tarihBitis = DateTimeToStringHelper.toTimeString(calisma.tarihBitis);
                const kadroIsim = BusinessHelper.Kadro.IdListtenIsimBirlestir(genelDurumHelper.SeciliKoroModel.tumKadrolar, calisma.kadroIdList, "Full");
                const sesGrupIsim = calisma.sesGrupIdList.length === 0 ? "" : (" (" + BusinessHelper.SesGrubu.IdListtenIsimBirlestir(genelDurumHelper.SeciliKoroModel.tumSesGruplari, calisma.sesGrupIdList) + ")")

                const { katilimDurumText, katilimDurumStyle, gerekce } = katilimDurumGetir(calisma, hesapCalisma, calismaZorunluMu);

                return <Columns key={calisma.id} mt={10}>
                    {appIcons.ScreenIcons.takvim}
                    <Rows style={{ maxWidth: "300px" }}>
                        <CsmText bold>{calisma.icerik}</CsmText>
                        <CsmText>{tarihBaslangic} - {tarihBitis}</CsmText>
                        <CsmText hasLightBottomBorder>{genelDurumHelper.translate("Hedef Kitle")}: {kadroIsim}{sesGrupIsim}</CsmText>
                        {yoklamaArabirimiKullanilsin &&
                            <Rows>
                                {katilimDurumText === "" ? null :
                                    <Rows>
                                        <CsmText>Katılım Durumunuz: </CsmText>
                                        <CsmText style={katilimDurumStyle}>{katilimDurumText}</CsmText>
                                    </Rows>
                                }
                                {gerekce == null ? null :
                                    <Columns>
                                        <CsmText>Gerekçe: </CsmText>
                                        <CsmText style={{ fontStyle: (gerekce === "" ? "italic" : undefined) }}>{gerekce === "" ? "Belirtilmemiş" : gerekce}</CsmText>
                                    </Columns>
                                }
                                {(eskiCalisma || !calismaZorunluMu) ? null :
                                    <Columns>
                                        <Button variant="contained" onClick={() => katilimDurumBildir(calisma, hesapCalisma)}>{(hesapCalisma == null || hesapCalisma.katilimDurum == null) ? "Katılım Durumu Bildir" : "Katılım Durumu Güncelle"}</Button>
                                    </Columns>
                                }
                                {generateYonetimYoklamaContent(calisma.id)}
                            </Rows>
                        }
                    </Rows>
                </Columns>
            })}
        </Rows>
    }

    // main render
    return (
        <MainLayout
            location={mainScreenLocations.takvim}
            genelDurumHelper={genelDurumHelper}
            hideHeader={false}
            hideMenu={false}>
            <VatanCalendarWithNavigation
                tatilleriGoster
                renderDate={renderDate}
                isEtkinlikDay={isEtkinlikDay}
                onSelectedDataChanged={(day) => setSeciliGun(day)}
                selectedDate={seciliGun}
                genelDurumHelper={genelDurumHelper}
                seciliGunIcerik={seciliGunIcerik}
                bosGunSecilebilir={false}
                etkinlikTakivimiKullanilsin={etkinlikTakivimiKullanilsin}
            />
        </MainLayout>
    );
}

function katilimDurumGetir(calisma: CalismaModel, hesapCalisma: HesapCalismaModel | null, calismaZorunluMu: boolean): { katilimDurumText: string; katilimDurumStyle: CSSProperties; gerekce: string | null } {
    if (calismaZorunluMu === false) {
        if (hesapCalisma != null && hesapCalisma.derseGercekKatilimDurum === EnmKatilimDurum.Katıldı)
            return { katilimDurumText: "Çalışmaya katıldınız", katilimDurumStyle: {}, gerekce: null }
        if (hesapCalisma != null && hesapCalisma.derseGercekKatilimDurum === EnmKatilimDurum["Geç Katıldı"])
            return { katilimDurumText: "Çalışmaya geç katıldınız", katilimDurumStyle: {}, gerekce: null }
        else
            return { katilimDurumText: "", katilimDurumStyle: {}, gerekce: null }
    }

    if (calisma.tarihBitis < DateTimeHelper.today()) {
        // eski çalışma
        if (hesapCalisma == null || hesapCalisma.derseGercekKatilimDurum === null)
            return { katilimDurumText: "", katilimDurumStyle: {}, gerekce: null }
        else if (hesapCalisma.derseGercekKatilimDurum === EnmKatilimDurum.Katıldı)
            return { katilimDurumText: "Çalışmaya katıldınız", katilimDurumStyle: { color: appColors.LIGHTGREEN }, gerekce: null }
        else if (hesapCalisma.derseGercekKatilimDurum === EnmKatilimDurum["Geç Katıldı"])
            return { katilimDurumText: "Çalışmaya geç katıldınız", katilimDurumStyle: { color: appColors.LIGHTYELLOW }, gerekce: null }
        else
            return { katilimDurumText: "Çalışmaya katılmadınız", katilimDurumStyle: { color: appColors.LIGHTRED }, gerekce: StringHelper.nullToEmpty(hesapCalisma?.katilmamaGerekce) }
    }
    else {
        if (hesapCalisma == null || hesapCalisma.katilimDurum == null)
            return { katilimDurumText: "BELİRTİLMEMİŞ, Lütfen katılım durumunuzu belirtin", katilimDurumStyle: { color: appColors.RED }, gerekce: null }
        else if (hesapCalisma.katilimDurum === EnmKatilimBildirmeDurum.Katılamıyorum)
            return { katilimDurumText: "Katılmayacağınızı bildirmişsiniz", katilimDurumStyle: { color: appColors.YELLOW }, gerekce: StringHelper.nullToEmpty(hesapCalisma?.katilmamaGerekce) }
        else if (hesapCalisma.katilimDurum === EnmKatilimBildirmeDurum.Belirsiz)
            return { katilimDurumText: "Belirsiz olduğunu bildirmişsiniz", katilimDurumStyle: { color: appColors.YELLOW }, gerekce: StringHelper.nullToEmpty(hesapCalisma?.katilmamaGerekce) }
        else
            return { katilimDurumText: "Katılacağınızı bildirmişsiniz", katilimDurumStyle: { color: appColors.GREEN }, gerekce: null }
    }
}

function determineSize<T>(size: VatanCalendarSizes, small: T, medium: T, large: T): T {
    if (size === "small")
        return small;
    else if (size === "medium")
        return medium;
    else
        return large;
}


export default TakvimScreen;
